import { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { Box } from "@chakra-ui/layout";
import { useToken } from "@chakra-ui/react";
import { PermBadge } from "features/transactions/components/PermBadge";
import { EditCell } from "features/transactions/TransactionsViewsSidebar/ActionCell";
import { DEFAULT_VIEW_ID } from "features/transactions/TransactionsViewsSidebar/constants";
import {
  isDefaultRow,
  isSelected,
} from "features/transactions/TransactionsViewsSidebar/utils";
import { useTableViewsGetSuspense } from "gen/endpoints/table-views/table-views";
import { TableViewReadData } from "gen/model/tableViewReadData";
import { TableViewReadDataList } from "gen/model/tableViewReadDataList";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { TableBox } from "shared/components/table/box/TableBox";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import {
  ActionCellClass,
  ClickableRowClass,
} from "shared/components/table/maybern/MaybernTableConstants";
import { rootRoute } from "shared/navigation/Routes";

export const TransactionsViewsSidebar: React.FC = () => {
  const { t } = useTranslation(["common", "views"]);
  const { data } = useTableViewsGetSuspense();
  const params = useParams<{ viewId?: string }>();

  const tableRef = useRef<AgGridReact<TableViewReadData>>(null);

  const columnDefs = useMemo(
    (): ColDef<TableViewReadDataList[number]>[] => [
      {
        colId: "name",
        resizable: false,
        field: "name",

        headerName: t("views:columns.name"),
        width: 280,
        cellStyle: {
          backgroundColor: "transparent",
        },
      },
      {
        cellStyle: {
          backgroundColor: "transparent",
        },
        headerName: t("views:columns.visibility"),
        colId: "visible",
        resizable: false,
        field: "visible_to",
        width: 100,
        cellRenderer: ({ data: view }: { data: TableViewReadData }) => {
          if (isDefaultRow(view)) {
            return null;
          }
          return (
            <Box display="flex" alignItems="center" justifyContent="center">
              <PermBadge view={view} />
            </Box>
          );
        },
      },
      {
        width: 50,
        cellStyle: {
          backgroundColor: "transparent",
        },
        colId: "actions",
        resizable: false,
        cellClass: ActionCellClass,

        cellRenderer: ({ data: view }: { data: TableViewReadData }) => {
          if (isDefaultRow(view)) {
            return null;
          }
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              width="100%"
            >
              <EditCell view={view} />
            </Box>
          );
        },
      },
    ],
    [t],
  );

  const dataWithDefault: TableViewReadData[] = useMemo(
    () =>
      [
        {
          id: DEFAULT_VIEW_ID,
          name: t("views:viewTypes.allTransactions"),
        } as TableViewReadData,
        ...data,
      ].sort(
        (a, b) =>
          new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime(),
      ),
    [data, t],
  );

  const navigate = useNavigate();
  const green = useToken("colors", "highlight");

  return (
    <TableBox
      boxProps={{
        sx: {
          border: "none",
          borderRadius: "none",
          boxShadow: "none",
        },
      }}
    >
      <MaybernTable
        ref={tableRef}
        onRowClicked={({ data, event }) => {
          if (event?.defaultPrevented || !data?.id) {
            return;
          }
          if (isDefaultRow(data)) {
            navigate(rootRoute({}).transactions({}).tableDraft({}).$);
          } else {
            navigate(
              rootRoute({}).transactions({}).tableDetails({
                viewId: data.id,
              }).$,
            );
          }
        }}
        columnDefs={columnDefs}
        rowData={dataWithDefault}
        suppressCellFocus={true}
        getRowStyle={({ data }) => {
          if (!data) {
            return;
          }
          const rowIsSelected = isSelected(params, data);
          return {
            backgroundColor: rowIsSelected ? green : "white",
          };
        }}
        getRowClass={({ data }) => {
          if (!data) {
            return;
          }
          const rowIsSelected = isSelected(params, data);
          if (rowIsSelected) {
            return;
          }
          return ClickableRowClass;
        }}
      />
    </TableBox>
  );
};

export default TransactionsViewsSidebar;
